import routePaths, { RoutePathType } from "routing/routePaths";

import { UserPermissionType } from "consts/UserPermissionTypes";
import UserPermissionTypes from "consts/UserPermissionTypes";
import lazyWithRetry from "utils/lazyWithRetry";

//NOTE: The following list of lazy imports MUST be in alphabetical order.
const About = lazyWithRetry(() => import("components/about/About"));
const CheckboxGroupList = lazyWithRetry(() => import("components/checkbox-groups/CheckboxGroupList"));
const CheckboxGroup = lazyWithRetry(() => import("components/checkbox-groups/CheckboxGroup"));
const CheckboxGroupAdmin = lazyWithRetry(() => import("components/checkbox-groups/CheckboxGroupAdmin"));
const CheckboxGroupCreate = lazyWithRetry(() => import("components/checkbox-groups/CheckboxGroupCreate"));
const CompoundQuestionCreate = lazyWithRetry(() => import("components/compound-questions/CompoundQuestionCreate"));
const CompoundQuestionList = lazyWithRetry(() => import("components/compound-questions/CompoundQuestionList"));
const Contract = lazyWithRetry(() => import("components/auth/Contract"));
const CrosstabCreate = lazyWithRetry(() => import("components/crosstabs/CrosstabCreate"));
const Crosstab = lazyWithRetry(() => import("components/crosstabs/Crosstab"));
const CrosstabList = lazyWithRetry(() => import("components/crosstabs/CrosstabList"));
const DeepProfile = lazyWithRetry(() => import("components/reports/deep-profiles/DeepProfile"));
const DeepProfileCreate = lazyWithRetry(() => import("components/reports/deep-profiles/DeepProfileCreate"));
const DashboardDefault = lazyWithRetry(() => import("components/dashboard/DashboardDefault"));
const Dashboard = lazyWithRetry(() => import("components/dashboard/Dashboard"));
const DeploymentSchemeCreate = lazyWithRetry(() => import("components/deployment-schemes/DeploymentSchemeCreate"));
const DeploymentSchemeUpdate = lazyWithRetry(() => import("components/deployment-schemes/DeploymentSchemeUpdate"));
const DeploymentSchemeList = lazyWithRetry(() => import("components/deployment-schemes/DeploymentSchemeList"));
const ForgotPassword = lazyWithRetry(() => import("components/auth/ForgotPassword"));
const Home = lazyWithRetry(() => import("components/home/Home"));
const Login = lazyWithRetry(() => import("components/auth/Login"));
const Logout = lazyWithRetry(() => import("components/auth/Logout"));
const MatrixQuestion = lazyWithRetry(() => import("components/matrix-questions/MatrixQuestion"));
const MatrixQuestionList = lazyWithRetry(() => import("components/matrix-questions/MatrixQuestionList"));
const MatrixQuestionCreate = lazyWithRetry(() => import("components/matrix-questions/MatrixQuestionCreate"));
const MatrixQuestionUpdate = lazyWithRetry(() => import("components/matrix-questions/MatrixQuestionUpdate"));
const MultiScoreCreate = lazyWithRetry(() => import("components/reports/multi-scores/MultiScoreCreate"));
const MultiScore = lazyWithRetry(() => import("components/reports/multi-scores/MultiScore"));
const MultiTimeviewDetail = lazyWithRetry(() => import("components/reports/multi-timeviews/MultiTimeview"));
const MultiTimeviewCreate = lazyWithRetry(() => import("components/reports/multi-timeviews/MultiTimeviewCreate"));
const NPTBusinessList = lazyWithRetry(() => import("components/nugget-preset-traits/NuggetPresetTraitBusinessList"));
const Profile = lazyWithRetry(() => import("components/profile/Profile"));
const Question = lazyWithRetry(() => import("components/questions/Question"));
const QuestionList = lazyWithRetry(() => import("components/questions/QuestionList"));
const QuestionFeed = lazyWithRetry(() => import("components/question-feeds/QuestionFeed"));
const QuestionFeedList = lazyWithRetry(() => import("components/question-feeds/QuestionFeedList"));
const ReportList = lazyWithRetry(() => import("components/reports/ReportList"));
const RequestPasswordReset = lazyWithRetry(() => import("components/auth/RequestPasswordReset"));
const ResetPassword = lazyWithRetry(() => import("components/auth/ResetPassword"));
const ScorecardDetail = lazyWithRetry(() => import("components/reports/scorecards/Scorecard"));
const ScorecardCreate = lazyWithRetry(() => import("components/reports/scorecards/ScorecardCreate"));
const Segment = lazyWithRetry(() => import("components/segments/Segment"));
const SegmentList = lazyWithRetry(() => import("components/segments/SegmentList"));
const StarRatingQuestionList = lazyWithRetry(() => import("components/star-rating-questions/StarRatingQuestionList"));
const SysAdmin = lazyWithRetry(() => import("components/sysadmin/SysAdmin"));
const TagList = lazyWithRetry(() => import("components/tags/TagList"));
const TagDetail = lazyWithRetry(() => import("components/tags/TagDetail"));
const Target = lazyWithRetry(() => import("components/targets/Target"));
const TargetList = lazyWithRetry(() => import("components/targets/TargetList"));
const TermsOfUse = lazyWithRetry(() => import("components/auth/TermsOfUse"));
const Toolkit = lazyWithRetry(() => import("components/toolkit/Toolkit"));
const TrendingQuestionsList = lazyWithRetry(
  () => import("components/sysadmin/trending-questions/TrendingQuestionsList"),
);
const UserRegister = lazyWithRetry(() => import("components/auth/UserRegister"));
const UserConfirm = lazyWithRetry(() => import("components/auth/UserConfirm"));
const WeightingScheme = lazyWithRetry(() => import("components/weighting-schemes/WeightingScheme"));
const WeightingSchemeList = lazyWithRetry(() => import("components/weighting-schemes/WeightingSchemeList"));

type RouteType = {
  path: RoutePathType;
  component: React.LazyExoticComponent<React.ComponentType<unknown>>;
  isProtected: boolean;
  permissions?: UserPermissionType[];
};

const sysadminWithInsightstore = [UserPermissionTypes.SYSADMIN, UserPermissionTypes.INSIGHTSTORE];

// TODO: Add permissions to rest of paths
const routes: RouteType[] = [
  {
    path: routePaths.TOOLKIT,
    component: Toolkit,
    isProtected: true,
  },
  {
    path: routePaths.ABOUT,
    component: About,
    isProtected: true,
  },
  {
    path: routePaths.TARGET,
    component: Target,
    isProtected: true,
    permissions: [UserPermissionTypes.TARGET_LIST, UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.TARGETS,
    component: TargetList,
    isProtected: true,
    permissions: [UserPermissionTypes.TARGET_LIST, UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.TRENDING_QUESTIONS,
    component: TrendingQuestionsList,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.NUGGET_PRESET_TRAIT_BUSINESS,
    component: NPTBusinessList,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.SYSADMIN,
    component: SysAdmin,
    isProtected: true,
    permissions: [UserPermissionTypes.SYSADMIN],
  },
  {
    path: routePaths.CHECKBOX_QUESTION,
    component: CheckboxGroup,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE], // non admin cb route
  },
  {
    path: routePaths.CHECKBOX_GROUP_CREATE,
    component: CheckboxGroupCreate,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.CHECKBOX_GROUP_DETAIL,
    component: CheckboxGroupAdmin,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.CHECKBOX_GROUPS,
    component: CheckboxGroupList,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.COMPOUND_QUESTION_CREATE,
    component: CompoundQuestionCreate,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.COMPOUND_QUESTIONS_ADMIN,
    component: CompoundQuestionList,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.WEIGHTING_SCHEME_CREATE,
    component: WeightingScheme,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.WEIGHTING_SCHEME_DETAIL,
    component: WeightingScheme,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.WEIGHTING_SCHEMES,
    component: WeightingSchemeList,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.MATRIX_QUESTION_ADMIN_CREATE,
    component: MatrixQuestionCreate,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.MATRIX_QUESTION,
    component: MatrixQuestion,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE], // non admin matrix route
  },
  {
    path: routePaths.MATRIX_QUESTION_ADMIN_DETAIL,
    component: MatrixQuestionUpdate,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.MATRIX_QUESTIONS_ADMIN,
    component: MatrixQuestionList,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.MULTI_SCORE_CREATE,
    component: MultiScoreCreate,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },

  {
    path: routePaths.MULTI_SCORE,
    component: MultiScore,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.SEGMENT,
    component: Segment,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.SEGMENTS,
    component: SegmentList,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.DEPLOYMENT_SCHEME_CREATE,
    component: DeploymentSchemeCreate,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.DEPLOYMENT_SCHEME_DETAIL,
    component: DeploymentSchemeUpdate,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.DEPLOYMENT_SCHEMES,
    component: DeploymentSchemeList,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.DEEP_PROFILE_CREATE,
    component: DeepProfileCreate,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.DEEP_PROFILE,
    component: DeepProfile,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.SCORECARD_CREATE,
    component: ScorecardCreate,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.MULTI_TIMEVIEW_CREATE,
    component: MultiTimeviewCreate,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.MULTI_TIMEVIEW,
    component: MultiTimeviewDetail,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.SCORECARD_DETAIL,
    component: ScorecardDetail,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.CROSSTAB_CREATE,
    component: CrosstabCreate,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.CROSSTAB,
    component: Crosstab,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.CROSSTABS,
    component: CrosstabList,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.REPORTS,
    component: ReportList,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.DASHBOARD,
    component: Dashboard,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.DASHBOARD_DEFAULT,
    component: DashboardDefault,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.HOME,
    component: Home,
    isProtected: true,
  },
  {
    path: routePaths.LOGIN,
    component: Login,
    isProtected: false,
  },
  {
    path: routePaths.REQUEST_PASSWORD_RESET,
    component: RequestPasswordReset,
    isProtected: false,
  },
  {
    path: routePaths.RESET_PASSWORD,
    component: ResetPassword,
    isProtected: false,
  },
  {
    path: routePaths.FORGOT_PASSWORD,
    component: ForgotPassword,
    isProtected: false,
  },
  {
    path: routePaths.TERMS_OF_USE,
    component: TermsOfUse,
    isProtected: true,
  },
  {
    path: routePaths.CONTRACT,
    component: Contract,
    isProtected: true,
  },
  {
    path: routePaths.QUESTION,
    component: Question,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.QUESTIONS,
    component: QuestionList,
    isProtected: true,
    permissions: [UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.QUESTION_FEED_CREATE,
    component: QuestionFeed,
    isProtected: true,
    permissions: [UserPermissionTypes.QUESTION_FEED_CREATE, UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.QUESTION_FEED_DETAIL,
    component: QuestionFeed,
    isProtected: true,
    permissions: [UserPermissionTypes.QUESTION_FEED_LIST, UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.QUESTION_FEEDS,
    component: QuestionFeedList,
    isProtected: true,
    permissions: [UserPermissionTypes.QUESTION_FEED_LIST, UserPermissionTypes.INSIGHTSTORE],
  },
  {
    path: routePaths.PROFILE,
    component: Profile,
    isProtected: true,
  },
  {
    path: routePaths.USER_REGISTRATION_CONFIRM,
    component: UserConfirm,
    isProtected: false,
  },
  {
    path: routePaths.USER_REGISTRATION,
    component: UserRegister,
    isProtected: false,
  },
  {
    path: routePaths.STAR_RATING_QUESTIONS,
    component: StarRatingQuestionList,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.TAG_DETAIL,
    component: TagDetail,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.TAGS,
    component: TagList,
    isProtected: true,
    permissions: sysadminWithInsightstore,
  },
  {
    path: routePaths.LOGOUT,
    component: Logout,
    isProtected: true,
  },
];

export const defaultPath = routePaths.HOME;

export const defaultRoute = { path: defaultPath, text: "Home" };

export const unprotectedRoutes = routes.filter((route) => !route.isProtected);

export const protectedRoutes = routes.filter((route) => route.isProtected);

/**
 * If permissions are supplied to the route, check that they exist within userPermissions.
 */
export const permissionProtectedRoutes = (userPermissions: UserPermissionType[]) =>
  protectedRoutes.filter((route) =>
    route?.permissions ? route.permissions.every((permission) => userPermissions.includes(permission)) : true,
  );

export default routes;
